/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    AUDIT_LOGGING_IMPLEMENTATION
 * Date:         2025-01-15
 * Modified By:  Cursor
 *
 * Description:
 * API client service for audit log endpoints. Provides functions to retrieve
 * audit logs by user, by admin, and recent activity.
 *
 * Related Screens / Modules:
 * - client/src/interfaces/auditLog.ts
 * - client/src/features/admin/users/components/UserAuditLog.tsx
 */

import axios from "axios";
import { apiUrl } from "../config/environment";
import { getAuthHeaders } from "../utils/authUtils";
import { AuditLogResponse } from "../interfaces/auditLog";

const base = `${apiUrl}/api/admin/audit-logs`;

/**
 * Get audit logs for specific user
 * [2025-01-15] Audit logging: Retrieve paginated audit logs for a user
 */
export async function getUserAuditLogs(
  userId: string,
  page: number = 1,
  limit: number = 20,
): Promise<AuditLogResponse> {
  const response = await axios.get(`${base}/user/${userId}`, {
    params: { page, limit },
    ...getAuthHeaders(),
  });

  return response.data;
}

/**
 * Get audit logs by admin
 * [2025-01-15] Audit logging: Retrieve paginated audit logs for an admin
 */
export async function getAdminAuditLogs(
  adminId: string,
  page: number = 1,
  limit: number = 20,
): Promise<AuditLogResponse> {
  const response = await axios.get(`${base}/admin/${adminId}`, {
    params: { page, limit },
    ...getAuthHeaders(),
  });

  return response.data;
}

/**
 * Get recent audit activity (all admins)
 * [2025-01-15] Audit logging: Retrieve recent audit activity across all admins
 */
export async function getRecentAuditActivity(
  page: number = 1,
  limit: number = 50,
): Promise<AuditLogResponse> {
  const response = await axios.get(`${base}/recent`, {
    params: { page, limit },
    ...getAuthHeaders(),
  });

  return response.data;
}
